import {Injectable} from '@angular/core';
import {RequestService} from "../request/request.service";
import {ArticleDataModel} from "./article-data.model";
import {PortalComponentModel, PortalComponentStatus} from "./portal-component.model";
import {LegacyInitializer} from "../initialization/legacy.initializer";
import {UserService} from "../user/user.service";
import {PortalDefinitionModel} from "./portal-definition.model";
import {PortalStyleModel} from "./portal-style.model";
import {PortalPageModel} from "./portal-page.model";
import {ReportDashboardModel} from "./report-dashboard.model";
import {UserProfileService} from '../user/user-profile.service';
import {ReplaySubject} from "rxjs";
import {UtilService} from "../util/util.service";
import {FeatureFlagsService} from "../feature-flag/feature-flags.service";
import {LocalizeService} from "../internationalization/localize.service";

@Injectable({
  providedIn: 'root'
})
export class PortalDataService {

  private articles: ArticleDataModel[] = [];

  private components: PortalComponentModel[] = [];

  private pages: PortalPageModel[] = [];

  private reportDashboards: ReportDashboardModel[] = [];

  private effectiveDefinition: PortalDefinitionModel = null;

  private userDashboard: string = "";

  private userPortal: string = "";

  private tables: { value: string, i18nKey: string }[] = [];

  portalLoaded: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  portalComponentsLoaded: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  currentPortalModalReferenceQualifierContext: any = null;

  constructor(private requestService: RequestService,
              private legacyInitializer: LegacyInitializer,
              private userService: UserService,
              private userProfileService: UserProfileService,
              private utilService: UtilService,
              private featureFlagsService: FeatureFlagsService,
              private localizeService: LocalizeService) {

    (window as any).NG_SHARED_PORTAL_DATA_SERVICE = this;

    this.legacyInitializer.legacyLoaded.subscribe((e) => {

      (window as any).WebsocketAPI.registerAPIImplementation("portalContent", (data: any, responseBody: any) => {
        this.articles = [];
        this.getArticles(0, 2).then((articles) => {
          this.portalLoaded.next(false);
        });
      });

      (window as any).WebsocketAPI.registerAPIImplementation("portalComponents", (data: any, responseBody: any) => {
        // Don't load portal data if not authenticated.
        if (this.userService.isAuthenticated()) {
          this.loadPortalData(true).then((d) => {
            this.portalComponentsLoaded.next(false);
          });
        }
      });

      // Look for 'create' table events that are relevant to portal and catalog, but only if the server side is not
      // broadcasting.

      // Matches PortalContentInvalidator.java for "portalComponents" broadcast
      const targetTables = [
        "PortalComponent",
        "PortalComponentParameter",
        "Dashboard",
        "PortalPage",
        "ReportDashboard",
        "PortalDefinition",
        "PortalStyle"
      ];

      const targetOperations = ["create", "update", "delete"];

      const messageBus = (window as any).KO_MESSAGE_BUS_BRIDGE;

      messageBus.subscribe(messageBus.CHANNELS.TABLE_EVENT,
        "#",
        async (data: any, envelope: any) => {
          if (targetOperations.includes(data.eventName) && targetTables.includes(data.tableName)) {
              // Don't load portal data if not authenticated.
              if (this.userService.isAuthenticated()) {
                this.loadPortalData(true).then((d) => {
                  this.portalComponentsLoaded.next(false);
                });
              }
          }
        });

      // Don't load portal data if not authenticated.
      if (this.userService.isAuthenticated()) {
        this.loadPortalData(true).then(async (d) => {
          this.portalComponentsLoaded.next(true);
        });
      }
    });
  }

  async getArticles(from: number, count: number) {

    const parameters = {
      from: from,
      count: count
    };

    if (this.articles.length < (from + count)) {
      const response: any = await this.makePortalControllerRequest("getArticleData", parameters);

      const data: any = JSON.parse(response.data);

      const existingIds = this.articles.map(a => a.id);
      const newKnowledge = data["articles"].filter(a => !existingIds.includes(a.id));
      newKnowledge.forEach((n) => {
        this.articles.push(n);
      })
    }
    return this.articles.slice(from, from + count);
  }

  async getAnnouncements() {
    // TODO should we cache this?
    const response: any = await this.makePortalControllerRequest("getAnnouncementData");

    try {
      const result = JSON.parse(response.data);
      return result.announcements;
    } catch (e) {
      console.error("PortalAnnouncements: cannot parse response", e);
      return [];
    }
  }

  getPages() {
    return this.pages;
  }

  getComponents() {
    return this.components;
  }

  getTables() {
    return this.tables;
  }

  getUserDashboard() {
    return this.userDashboard;
  }

  getUserPortal() {
    return this.userPortal;
  }

  getPrivateReportDashboards() {
    return this.reportDashboards.filter((d) => d.Private.value === true);
  }

  getPublicReportDashboards() {
    return this.reportDashboards.filter((d) => d.Private.value === false);
  }

  getReportDashboards() {
    return this.reportDashboards;
  }

  async getEffectiveDefinition() {
    return this.effectiveDefinition;
  }

  async loadPortalData(forceReload: boolean = true) {
    return Promise.all([
      this.getAndCacheResource("getPortalPages", "pages", forceReload),
      this.getAndCacheResource("getReportDashboards", "reportDashboards", forceReload),
      this.getAndCacheResource("getEffectivePortalDefinition", "effectiveDefinition", forceReload),
      this.getComponentsAsync(forceReload),
      this.getAndCacheResource("getTables", "tables", forceReload),
      this.getUserDashboardAsync(forceReload),
      this.getUserPortalAsync(forceReload)
    ]);
  }

  private async makePortalControllerRequest(method: string, parameters: any = {}) {
    return await this.requestService.createRequest()
      .controller("servicely.core.Portal")
      .method(method)
      .data(parameters)
      .execute();
  }

  private async getComponentsAsync(forceReload: boolean = false) {

    if (!this.components || this.components.length === 0 || forceReload) {

      let components: PortalComponentModel[] = [];

      try {
        const response: any = await this.makePortalControllerRequest("getPortalComponents", { parameters: {} });

        const responseData: { component: PortalComponentModel, componentHash: string }[] = response.data;

        if (responseData) {
          const newComponents: PortalComponentModel[] = responseData.map((c) => {
            c.component.componentHash = c.componentHash;
            return c.component;
          });

          newComponents.forEach((nc) => {
            const matchingComponent = this.components.find((c) => c.id === nc.id);

            if (matchingComponent) {
              nc.componentStatus = nc.componentHash === matchingComponent.componentHash ? PortalComponentStatus.Existing : PortalComponentStatus.Updated;
            } else {
              nc.componentStatus = PortalComponentStatus.New;
            }

            nc.localizeTaggedTemplates = this.localizeService.extractLocalizeTaggedTemplates([nc.Component, nc.ServerScript], 'portal', "PortalComponent", nc.id);
          });

          components = newComponents;
        }
      } catch (e) {
        console.error("Exception retrieving component list: ", e);
      }

      if (components) {
        this.components = components;
      }
    }

    return this.components;
  }

  private async getAndCacheResource(method: string, fieldName: string, forceReload: boolean = false) {
    if (!this[fieldName] || this[fieldName].length === 0 || forceReload) {

      let resources: any[] = [];

      try {
        const response: any = await this.makePortalControllerRequest(method, { parameters: {} });

        const responseData: any[] = response.data;

        if (responseData) {
          resources = responseData;
        }
      } catch (e) {
        console.error(`Exception retrieving ${method} resources: `, e);
      }

      if (resources) {
        this[fieldName] = resources;
      }
    }

    return this[fieldName];
  }

  async saveDashboard(id: string, definition: string) {

    const requestData = {
      id: id,
      definition: definition,
    };

    let result: string = "";

    try {
      const response: any = await this.makePortalControllerRequest("saveDashboard", requestData);

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception saving dashboard: ", e);
      result = "error";
    }

    await this.getAndCacheResource("getPortalPages", "pages", true);
    await this.getAndCacheResource("getReportDashboards", "reportDashboards", true);

    return result;
  }

  async getUserDashboardAsync(forceReload: boolean) {

    let result: string = "";

    try {
      const responseData: any = await this.userProfileService.getUserProfile(false);

      if (responseData) {
        result = responseData['userDashboard'];
      }
    } catch (e) {
      console.error("Exception getting user dashboard id: ", e);
      result = "error";
    }

    if (result) {
      this.userDashboard = result;
    }
    return result;
  }

  async updateUserDashboard(dashboardId: string) {

    const requestData = {
      key: "userDashboard",
      value: dashboardId,
    };

    let result: string = "";

    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("updateUserProfileSetting")
        .data(requestData)
        .execute();

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception saving user dashboard id: ", e);
      result = "error";
    }

    return result;
  }

  async getUserPortalAsync(forceReload: boolean) {

    let result: string = "";

    try {
      const responseData: any = await this.userProfileService.getUserProfile(false);

      if (responseData) {
        result = responseData['userPortalDefinition'];
      }
    } catch (e) {
      console.error("Exception getting user portal definition id: ", e);
      result = "error";
    }

    if (result) {
      this.userPortal = result;
    }

    return result;
  }

  async updateUserPortalDefinition(definitionId: string) {

    const requestData = {
      key: "userPortalDefinition",
      value: definitionId,
    };

    let result: string = "";

    try {
      const response: any = await this.requestService.createRequest()
        .controller("servicely.core.User")
        .method("updateUserProfileSetting")
        .data(requestData)
        .execute();

      const responseData: any = response.data;

      if (responseData) {
        result = JSON.stringify(responseData);
      }
    } catch (e) {
      console.error("Exception saving user portal definition id: ", e);
      result = "error";
    }

    return result;
  }

  async getDashboardCreatorId(id: string) {
    const requestData = {
      id: id
    };
    const response: any = await this.makePortalControllerRequest("getDashboardCreatorId", requestData);

    return response?.data?.id || "";
  }

  setCurrentPortalModalReferenceQualifierContext(context: any) {
    this.currentPortalModalReferenceQualifierContext = context;
  }

  getCurrentPortalModalReferenceQualifierContext() {
    return this.currentPortalModalReferenceQualifierContext;
  }
}
